import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Briefcase, Sparkles } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatusPill, BeltBadge, Empty, Field } from "../../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { CategorySelect, SectorSelect } from "../../components/JobCategories";
import { getGeo } from "../../lib/currency";

const COUNTRY_NAMES = () => { const g = getGeo(); const names = (g.countries || []).map((c) => c.name); return names.includes("Ghana") ? names : ["Ghana", ...names]; };
const defaultCountry = () => (COUNTRY_NAMES().includes(getGeo().country_name) ? getGeo().country_name : "Ghana");

const BLANK = { title: "", description: "", location: "", job_type: "Full-time", skills: "", min_belt: "none", min_experience: 0, salary_range: "", openings: 1, category: "Other", sector: "Private Sector", country: "Ghana" };

const JobForm = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ ...BLANK, country: defaultCountry() });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const { data } = await api.post("/jobs", { ...f, skills: f.skills.split(","), min_experience: Number(f.min_experience) || 0, openings: Number(f.openings) || 1 });
      toast.success("Job posted - automatch is ready");
      setF({ ...BLANK, country: defaultCountry() });
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="job-dialog">
        <DialogHeader><DialogTitle className="font-display">Post a job</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Job title"><input className="dx-input" value={f.title} onChange={set("title")} data-testid="job-title-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Category"><CategorySelect value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="job-category-select" /></Field>
            <Field label="Sector"><SectorSelect value={f.sector} onChange={(v) => setF({ ...f, sector: v })} testid="job-sector-select" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country">
              <select className="dx-input" value={f.country} onChange={set("country")} data-testid="job-country-select">
                {COUNTRY_NAMES().map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="City / town"><input className="dx-input" value={f.location} onChange={set("location")} placeholder="e.g. Accra, Lagos, Remote" data-testid="job-location-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select className="dx-input" value={f.job_type} onChange={set("job_type")} data-testid="job-type-select">
                {["Full-time", "Part-time", "Contract", "Temporary", "Internship"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Required skills (comma separated)"><input className="dx-input" value={f.skills} onChange={set("skills")} placeholder="Customer Service, Excel" data-testid="job-skills-input" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Min. belt">
              <select className="dx-input" value={f.min_belt} onChange={set("min_belt")} data-testid="job-belt-select">
                <option value="none">Any</option><option value="yellow">Yellow</option><option value="green">Green</option><option value="black">Black</option>
              </select>
            </Field>
            <Field label="Min. years"><input type="number" min="0" className="dx-input" value={f.min_experience} onChange={set("min_experience")} data-testid="job-experience-input" /></Field>
            <Field label="Openings"><input type="number" min="1" className="dx-input" value={f.openings} onChange={set("openings")} data-testid="job-openings-input" /></Field>
          </div>
          <Field label="Salary range"><input className="dx-input" value={f.salary_range} onChange={set("salary_range")} placeholder="e.g. 2,000 - 3,000 / month (local currency)" data-testid="job-salary-input" /></Field>
          <Field label="Description"><textarea rows={4} className="dx-input" value={f.description} onChange={set("description")} data-testid="job-description-input" /></Field>
          <button className="dx-btn-gold w-full" disabled={f.title.length < 2} onClick={save} data-testid="job-save-btn">Post job</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function EmployerJobs() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const load = () => api.get("/employer/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);
  const open = params.get("new") === "1";

  return (
    <div>
      <PageHeader eyebrow="Recruitment" title={user.role === "admin" ? "All job postings" : "Job postings"} subtitle="Open a job to see automatched candidates and manage the hiring pipeline."
        action={user.role === "employer" && <button className="dx-btn-gold" onClick={() => setParams({ new: "1" })} data-testid="jobs-new-btn"><Plus className="w-4 h-4" />Post a job</button>} />
      {jobs.length === 0 ? <Empty icon={Briefcase} title="No jobs posted yet" text="Post your first job and automatch will rank candidates instantly." testid="employer-jobs-empty" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((j) => (
            <Link key={j.id} to={`/app/employer/jobs/${j.id}`} className="dx-card p-6 hover:-translate-y-0.5 transition-transform" data-testid={`employer-job-${j.id}`}>
              <div className="flex justify-between gap-2"><h3 className="font-display font-semibold text-lg text-[#250E31]">{j.title}</h3><StatusPill status={j.status} /></div>
              <div className="text-sm text-[#6b5a73]">{user.role === "admin" ? `${j.company_name} · ` : ""}{j.location}{j.country ? `, ${j.country}` : ""} · {j.job_type}</div>
              {j.category && <div className="text-xs font-semibold text-[#5B2A6E] mt-1">{j.category}</div>}
              <div className="mt-3">{j.min_belt !== "none" && <BeltBadge belt={j.min_belt} />}</div>
              <div className="flex items-center justify-between mt-5 text-xs text-[#6b5a73]">
                <span>{j.applications} applicants · {j.hired} hired</span>
                <span className="flex items-center gap-1 text-[#5B2A6E] font-semibold"><Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />Automatch</span>
              </div>
              <div className="text-xs text-[#8a7a92] mt-1">Posted {fmtDate(j.created_at)}</div>
            </Link>
          ))}
        </div>
      )}
      <JobForm open={open} onOpenChange={(o) => !o && setParams({})} onSaved={() => { setParams({}); load(); }} />
    </div>
  );
}
